import { useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { EligibleProductStatus, ProductBundle } from '@typesApp/Product'
import { eligibleProductController } from '@hooks/eligible-product-check/eligibleProductCheckController'
import { useCart } from '@hooks/useCart'
import { useClaimRecord } from '@hooks/useClaimRecord'
import { useFetchClaimRecord } from '@hooks/useFetchClaimRecord'
import { useSessionInfo } from '@hooks/useSessionInfo'
import { useCheckEligibleProductsMutation } from '@redux/queries/claimRecords'
import {
  setEligibleProductCheckError,
  setEligibleProductCheckInProgress,
  setEligibleProductCheckResult,
} from '@redux/slices/claimStatusSlice'
import { logger } from '@utils/logger'

export const useCartBundleChangeEventHandler = (): (() => void) => {
  const dispatch = useDispatch()
  const { fetchCartBundle } = useCart()
  const { claimRecordId } = useClaimRecord()
  const { fetchClaimRecord } = useFetchClaimRecord()
  const [triggerCheck] = useCheckEligibleProductsMutation()

  const { isActiveDataChecked, isEligibleProductCheckInProgress } = useSessionInfo()

  const performCheck = useCallback(async () => {
    const service = {
      fetchClaimRecord,
      fetchCartBundle,
      fetchEligibleProducts: (products: ProductBundle[]) => triggerCheck(products).unwrap(),

      onComplete: async (status: EligibleProductStatus) => {
        dispatch(setEligibleProductCheckResult(status))
      },
      onError: async () => {
        dispatch(setEligibleProductCheckError())
      },
    }

    const controller = eligibleProductController(service, claimRecordId)
    await controller.checkEligibility()
  }, [dispatch, triggerCheck, fetchClaimRecord, fetchCartBundle, claimRecordId])

  return useCallback(() => {
    if (!isActiveDataChecked || isEligibleProductCheckInProgress) return

    dispatch(setEligibleProductCheckInProgress(true))
    performCheck()
      .catch(logger.warn)
      .finally(() => dispatch(setEligibleProductCheckInProgress(false)))
  }, [dispatch, performCheck, isActiveDataChecked, isEligibleProductCheckInProgress])
}
